import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as i18n from 'i18next';
import { State } from 'reducers';
import { Device, BuildingId, DeviceClass, DeviceFamily } from 'shared/models';
import { getBuildingDeviceGroupNames } from 'selectors/buildingsSelectors';
import { ErrorMessage } from 'ui/shared/messages';
import { CheckField, LegacySelectField, TextField } from 'ui/shared/controls';
import { Loading } from 'ui/shared/loading';
import { Permission } from 'shared/permissions';
import { useForm, useSession } from 'hooks';
import { useDeviceTypes } from 'hooks/useDeviceTypes/useDeviceTypes';
import { useDeviceConfigs } from 'hooks/useDeviceConfigs/useDeviceConfigs';
import { sortNamesBy } from 'logic/sortNames';
import { DeviceConfigField } from 'ui/devices/deviceConfigField/DeviceConfigField';

require('./EditDeviceForm.css');

const nullValue = 'none';

const makeGroupNameOptions = (t: i18n.TFunction, groupNames: string[]) =>
    [
        // Note: couldn't use null or '' for the no-group value... Semantic UI mishandled it
        { value: nullValue, text: t('DEVICES.NO_GROUP') },
        ...groupNames.sort().map(name => ({ value: name, text: name })),
    ].map(item =>
        <option
            key={ `group-${item.text}` }
            value={ item.value }
        >
            { item.text }
        </option>
    );

export type Props = {
    busy: boolean;
    device: Device;
    buildingId: BuildingId;
    readOnly: boolean;
    pin: string;
    pinPending: boolean;
    errorMessage: string;
    onSubmit(device: Device): void;
    onClickCancel(): void;
    onClickDelete(): void;
    onCreatePin(): void;
};

export const EditDeviceForm = ({ busy, device, buildingId, pin, pinPending, errorMessage, readOnly, onSubmit, onClickCancel, onClickDelete, onCreatePin }: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();
    const { data, dirty, handleChange, handleSubmit } = useForm('device', device, onSubmit, { nullValue, resetOn: [ device?.id ] });

    const { deviceTypes, deviceTypeOrder, isLoadingDeviceTypes } = useDeviceTypes();
    const { deviceConfigs, deviceConfigIds, loading: isLoadingDeviceConfigs } = useDeviceConfigs(buildingId);

    const groupNames = useSelector((state: State) => buildingId ? getBuildingDeviceGroupNames(state, { buildingId }) : []);
    const isReady = !isLoadingDeviceTypes && !isLoadingDeviceConfigs;

    const deviceType = deviceTypes[data.deviceTypeId];
    const deviceFamily = deviceType?.deviceFamily;

    const isBillingDeviceType = data?.deviceClass === DeviceClass.Modem ||
        data?.deviceClass === DeviceClass.ATA ||
        data?.deviceClass === DeviceClass.SIMCard;

    const isDisplay = data?.deviceClass === DeviceClass.Display;
    const supportsPin =
        isDisplay && [
            DeviceFamily.MatisseR4,
            DeviceFamily.MatisseR5,
            DeviceFamily.TTG,
            DeviceFamily.MatisseFlexR2,
        ].includes(deviceType.deviceFamily!);

    //
    // Calculate which device types are in the selected device family
    //
    const compatibleDeviceConfigs =
        deviceConfigIds
            .map(id => deviceConfigs[id])
            .filter(dc => dc.properties.deviceFamily === deviceFamily)
            .sort(sortNamesBy(dc => dc.displayName));

    // Anything except Generic can have a device config
    const canHaveDeviceConfig = !!deviceType?.deviceFamily;

    // Update the readOnly flag to consider user permissions
    readOnly = !hasPermission(buildingId, Permission.EditDevice) || (readOnly && !hasPermission(null, Permission.Admin));

    // Don't render anything if either the building or the device types haven't loaded yet
    if (!isReady || !hasPermission(buildingId, Permission.ViewDevices)) {
        return null;
    }
    return (
        <form
            id="edit-device-form"
            onSubmit={ handleSubmit }
            className={ `ui form${errorMessage ? ' error' : ''}${busy ? ' loading' : ''}${dirty ? ' dirty' : ''}` }
        >
            <TextField
                id="edit-device-display-name"
                name="displayName"
                className="_name"
                labelKey="DEVICES.EDIT_DIALOG.FIELD.DISPLAY_NAME"
                maxLength={ 32 }
                autoComplete="off"
                readOnly={ readOnly }
                value={ data.displayName || '' }
                onChange={ handleChange }
            />
            {
                !isBillingDeviceType &&
                <TextField
                    id="edit-device-elevator-id"
                    name="properties.elevatorId"
                    className="_elevator-id"
                    labelKey="DEVICES.EDIT_DIALOG.FIELD.ELEVATOR_ID"
                    autoComplete="off"
                    readOnly={ !hasPermission(buildingId, Permission.EditDevice) }
                    value={ data.properties.elevatorId || '' }
                    onChange={ handleChange }
                />
            }

            <LegacySelectField
                id="edit-device-type"
                className="_type"
                required={ true }
                labelKey="DEVICES.EDIT_DIALOG.FIELD.DEVICE_TYPE"
                readOnly={ !hasPermission(buildingId, Permission.EditDevice) }
                input={ {
                    /* KILL: remove this input attribute once redux-form is gone */
                    name: "deviceTypeId",
                    value: data.deviceTypeId,
                    onChange: handleChange,
                } }
            >
                { deviceTypeOrder
                    .filter(id => deviceTypes[id].deviceClass === device.deviceClass)
                    .map(id =>
                        <option
                            key={ id }
                            value={ id }
                        >
                            { deviceTypes[id].displayName }
                        </option>
                    ) }
            </LegacySelectField>
            {
                !isBillingDeviceType &&
                <>
                    <LegacySelectField
                        id="edit-device-group"
                        className="_group"
                        labelKey="DEVICES.EDIT_DIALOG.FIELD.GROUP"
                        allowAdditions={ true }
                        readOnly={ !hasPermission(buildingId, Permission.EditDevice) }
                        input={ {
                            className: "ui fluid search dropdown",
                            name: 'groupName',
                            maxLength:  32,
                            value: data.groupName || nullValue,
                            onChange: handleChange,
                        } }
                    >
                        { makeGroupNameOptions(t, groupNames) }
                    </LegacySelectField>
        
                    <CheckField
                        id="edit-device-pi"
                        className="_pi"
                        labelKey="LABEL.PI"
                        input={ {
                            name: 'properties.pi',
                            checked: data.properties.pi,
                            onChange: handleChange,
                        } }
                    />
        
                    <DeviceConfigField
                        id="edit-device-config"
                        className="_config"
                        name="deviceConfigId"
                        value={ data.deviceConfigId }
                        deviceConfigs={ compatibleDeviceConfigs }
                        onChange={ handleChange }
                        readOnly={ !hasPermission(buildingId, Permission.EditDevice) }
                        disabled={ !canHaveDeviceConfig }
                        buildingId={ buildingId }
                        deviceTypeId={ data.deviceTypeId }
                        isPI={ !!device.properties.pi }
                    />
                </>
            }

            {
                (data?.deviceClass === DeviceClass.Modem ||
                data?.deviceClass === DeviceClass.ATA) &&
                hasPermission(null, Permission.StandardRoles) &&
                <TextField
                    id="edit-modem-form_serial-number"
                    name="properties.serialNumber"
                    labelKey="DEVICES.EDIT_DIALOG.FIELD.SERIAL_NUMBER"
                    maxLength={ 32 }
                    autoComplete="off"
                    required={ true }
                    value={ data.properties.serialNumber || '' }
                    onChange={ handleChange }
                />
            }

            {
                data?.deviceClass === DeviceClass.ATA &&
                hasPermission(null, Permission.StandardRoles) &&
                <TextField
                    id="edit-ata-form_mac-address"
                    name="macAddress"
                    labelKey="DEVICES.EDIT_DIALOG.FIELD.MAC_ADDRESS"
                    maxLength={ 32 }
                    autoComplete="off"
                    required={ true }
                    value={ data.macAddress?.replace(/:/g, '') }
                    onChange={ handleChange }
                />
            }

            {
                data?.deviceClass === DeviceClass.SIMCard &&
                hasPermission(null, Permission.StandardRoles) &&
                <TextField
                    id="add-sim-card-form_imei"
                    name="properties.imei"
                    labelKey="DEVICES.EDIT_DIALOG.FIELD.IMEI"
                    maxLength={ 32 }
                    autoComplete="off"
                    required={ true }
                    value={ data.properties?.imei || '' }
                    onChange={ handleChange }
                />
            }

            <div className="_buttons">
                { dirty &&
                    <button
                        className="ui primary submit button _save"
                        type="submit"
                        disabled={ busy }
                    >
                        { t('COMMAND.SAVE') }
                    </button>
                }
                <button
                    className="ui button _cancel"
                    type="button"
                    disabled={ busy }
                    onClick={ onClickCancel }
                >
                    { t(dirty ? 'COMMAND.CANCEL' : 'COMMAND.CLOSE') }
                </button>

                <span className="_pin">
                    { supportsPin && hasPermission(buildingId, Permission.EditDevice) &&
                        (pin
                            ? pin
                            : pinPending
                                ? <Loading/>
                                : <button
                                    type="button"
                                    onClick={ () => onCreatePin() }
                                    className="ui button"
                                >
                                    { t('COMMAND.CREATE_PIN') }
                                </button>
                        )
                    }
                </span>

                { hasPermission(buildingId, Permission.DeleteDevice) &&
                    <button
                        className="ui red basic button _delete"
                        type="button"
                        disabled={ busy }
                        onClick={ onClickDelete }
                    >
                        { t('COMMAND.DELETE') }
                    </button>
                }
            </div>

            <ErrorMessage
                className="_error"
                message={ errorMessage }
            />
        </form>
    );
};
